// RFC 8785 (JCS) canonical JSON serializer.
//
// Byte-for-byte compatible with the authoritative .NET implementation at
// libs/canonical-json/CanonicalJsonSerializer.cs. This is the single TS port
// shared across all module browser bundles and dev probes — any drift here
// would silently invalidate every signature, so it lives in one place.
//
// Values: objects, arrays, strings, numbers, booleans, nulls.
//
// Numbers (RFC 8785 §3.2.2.3) are ECMAScript's Number::toString (ECMA-262
// §7.1.12.1), which is JavaScript's own `String(number)`: shortest round-trip
// digits, exponent form at 1e21 and above and below 1e-6, `-0` as `0`. The
// .NET side formats the same way (CanonicalJsonSerializer.FormatEcma262); both
// are pinned by vectors/numbers.json. NaN and the infinities are refused.
// (docs/cid-governance.md M10: the float path. Callers that must stay
// integer-only check that themselves, e.g. libs/bitcash-code's `J`, which
// refuses any number that is not a safe integer before it gets here.)
//
// Key ordering: UTF-16 code-unit lexicographic — the default JS string
// comparison; matches StringComparison.Ordinal on the C# side.
//
// String escaping per RFC 8785 §3.2.2.2:
//   U+0008..U+000D → \b \t \n \f \r
//   U+0022 → \"
//   U+005C → \\
//   Other U+0000..U+001F → \uXXXX (lowercase hex)
//   Everything else: literal UTF-8.

export type CanonicalValue =
    | string
    | number
    | boolean
    | null
    | readonly CanonicalValue[]
    | { readonly [key: string]: CanonicalValue };

const encoder = new TextEncoder();

export function canonicalize(value: CanonicalValue): Uint8Array {
    const out: number[] = [];
    writeValue(value, out);
    return new Uint8Array(out);
}

export function canonicalizeToString(value: CanonicalValue): string {
    return new TextDecoder('utf-8').decode(canonicalize(value));
}

function writeValue(v: CanonicalValue, out: number[]): void {
    if (v === null) { pushAscii('null', out); return; }
    if (typeof v === 'boolean') { pushAscii(v ? 'true' : 'false', out); return; }
    if (typeof v === 'number') {
        if (!Number.isFinite(v)) throw new Error('Canonical JSON cannot serialize NaN or Infinity.');
        // ECMA-262 Number::toString; `String(-0)` is already "0".
        pushAscii(String(v), out);
        return;
    }
    if (typeof v === 'string') { writeString(v, out); return; }
    if (Array.isArray(v)) { writeArray(v, out); return; }
    writeObject(v as { readonly [key: string]: CanonicalValue }, out);
}

function writeObject(obj: { readonly [key: string]: CanonicalValue }, out: number[]): void {
    const keys = Object.keys(obj).sort();
    const seen = new Set<string>();
    out.push(0x7b); // {
    let first = true;
    for (const k of keys) {
        if (seen.has(k)) throw new Error(`Duplicate canonical JSON key: ${JSON.stringify(k)}`);
        seen.add(k);
        if (!first) out.push(0x2c); // ,
        first = false;
        writeString(k, out);
        out.push(0x3a); // :
        writeValue(obj[k] as CanonicalValue, out);
    }
    out.push(0x7d); // }
}

function writeArray(arr: readonly CanonicalValue[], out: number[]): void {
    out.push(0x5b); // [
    for (let i = 0; i < arr.length; i++) {
        if (i > 0) out.push(0x2c);
        writeValue(arr[i] as CanonicalValue, out);
    }
    out.push(0x5d); // ]
}

function writeString(s: string, out: number[]): void {
    out.push(0x22); // "
    for (const ch of s) {
        const cp = ch.codePointAt(0);
        if (cp === undefined) continue;
        switch (cp) {
            case 0x08: pushAscii('\\b', out); continue;
            case 0x09: pushAscii('\\t', out); continue;
            case 0x0a: pushAscii('\\n', out); continue;
            case 0x0c: pushAscii('\\f', out); continue;
            case 0x0d: pushAscii('\\r', out); continue;
            case 0x22: pushAscii('\\"', out); continue;
            case 0x5c: pushAscii('\\\\', out); continue;
            default:
                if (cp < 0x20) {
                    pushAscii(`\\u${cp.toString(16).padStart(4, '0')}`, out);
                } else {
                    for (const b of encoder.encode(ch)) out.push(b);
                }
        }
    }
    out.push(0x22);
}

function pushAscii(s: string, out: number[]): void {
    for (let i = 0; i < s.length; i++) out.push(s.charCodeAt(i));
}
